import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldGroup } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { parseError } from '../../../../lib/errors'
import { type COIDocument, remarksEnabled, updateCOISettings } from '../../../../models/coi'
import { selectionLabel } from './COIDocumentsTab'

/** One certificate's own settings — Cai staff only. */
export function CertificateSettingsDialog({
  document,
  orgId,
  onOpenChange,
  onSaved,
}: {
  document: COIDocument | null
  orgId?: string
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const [description, setDescription] = useState('')
  const [enableRemarks, setEnableRemarks] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Keyed off the document rather than the open flag: the dialog is reused for
  // every row, so reopening it on another certificate has to reload the values.
  useEffect(() => {
    if (!document) return
    setDescription(document.descriptionOfOperations ?? '')
    setEnableRemarks(remarksEnabled(document))
  }, [document])

  if (!document) return null

  const isDirty =
    description !== (document.descriptionOfOperations ?? '') || enableRemarks !== remarksEnabled(document)

  async function handleSave() {
    if (!document) return
    setIsSaving(true)
    try {
      await updateCOISettings(document.id, { descriptionOfOperations: description, enableRemarks }, orgId)
      toast.success('Certificate settings saved')
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={!!document} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Certificate settings</DialogTitle>
          <DialogDescription>
            {selectionLabel(document)} · v{document.version}
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <Label htmlFor="coi-description">Description of operations</Label>
            <Textarea
              id="coi-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="e.g. Certificate holder is named as additional insured."
              className="h-28"
            />
            <FieldDescription className="text-sm">
              Printed in the DESCRIPTION OF OPERATIONS box of every holder on this certificate that has none
              of its own. A holder with its own description keeps that one. Saving rebuilds every holder.
            </FieldDescription>
          </Field>

          <Field orientation="horizontal">
            <div className="flex-1">
              <Label className="text-md mb-1">Allow a vehicle schedule</Label>
              <FieldDescription className="text-sm">
                When off, the include trucks/trailers choice is hidden when adding a holder to this
                certificate — for Cai staff too — and no holder can be given one.
              </FieldDescription>
            </div>
            <Switch checked={enableRemarks} onCheckedChange={setEnableRemarks} />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button loading={isSaving ? 'Saving…' : undefined} disabled={!isDirty} onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
